import type { ReactNode } from 'react';
import Layout from '../../../../components/layout/Layout';
import Breadcrumb from '../../../../components/layout/Breadcrumb';
import ArticleHero from '../../../../components/news/ArticleHero';
import PullQuote from '../../../../components/news/PullQuote';
import KeyPointsBox from '../../../../components/news/KeyPointsBox';
import CommentBox from '../../../../components/news/CommentBox';
import SourceCard from '../../../../components/news/SourceCard';
import RelatedCards from '../../../../components/news/RelatedCards';

/* ---- In-article inline parts (editorial layout; no section bands) ---- */

function SectionHeading({ id, en, children }: { id: string; en: string; children: ReactNode }) {
  return (
    <div id={id} className="scroll-mt-28 mt-14 mb-6">
      <span aria-hidden="true" className="block w-10 h-px bg-[#a38e65] mb-4" />
      <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-2">{en}</span>
      <h2 className="jba-display font-medium text-[#1a2942] text-[24px] sm:text-[28px] leading-[1.4]">{children}</h2>
    </div>
  );
}

function SourceShot({ src, alt, media, url }: { src: string; alt: string; media: string; url: string }) {
  return (
    <figure className="my-9">
      <img src={src} alt={alt} loading="lazy" className="w-full h-auto border border-[#e2ddd2] shadow-[0_3px_20px_rgba(26,41,66,0.09)]" />
      <figcaption className="mt-2.5 text-[13px] text-[#8f7a56] font-['Noto_Serif_JP',serif] border-l-2 border-[#a38e65] pl-3 leading-[1.7]">
        Source: {media} (<a href={url} target="_blank" rel="noopener noreferrer" className="underline decoration-[#a38e65]/50 break-all hover:text-[#1a2942] transition-colors">{url}</a>)
      </figcaption>
    </figure>
  );
}

function Stat({ children }: { children: ReactNode }) {
  return <span className="font-['Playfair_Display',serif] text-[#8f7a56] text-[1.55em] font-bold tracking-tight px-[2px] align-[-0.06em]">{children}</span>;
}

const P = "text-[#2a2a2a] text-[17px] sm:text-[18px] leading-[1.9] font-['Noto_Serif_JP',serif]";

export default function RafflesLondonCampaignPageEN() {
  const breadcrumbs = [
    { label: 'Home', href: '/en' },
    { label: 'News', href: '/en/news' },
    { label: 'Raffles London "The Butler Did It" Campaign Chapter 2' }
  ];

  const article = {
    category: 'Industry Trends',
    date: 'September 17, 2025',
    title: 'Raffles London "The Butler Did It" Campaign Chapter 2',
    subtitle: "Innovative Initiative Set in London's Historic Building",
    heroImage: '/images/generated/jba-fs-111-news_raffles-london-campaign.webp',
    lead: 'Raffles Hotels & Resorts announces Chapter 2 of "The Butler Did It" campaign, now set in London\'s historic building The OWO.\n\nFeaturing British actor Henry Golding, this campaign is a notable initiative expressing the modern value of hyper-personalized butler service through a cinematic approach.',
    comment: 'Raffles London\'s "The Butler Did It" Campaign Chapter 2 is an outstanding initiative brilliantly expressing the modern evolution of butler service, with which our association is deeply impressed.\n\nThe concept of "hyper-personalization" particularly resonates deeply with the spirit of "ichigo ichie omotenashi" (once-in-a-lifetime hospitality) that we pursue.\n\nThe cinematic expression set in the historic building The OWO demonstrates to the world that butler service has cultural and artistic value beyond mere profession.\n\nWe expect such international initiatives to greatly contribute to the development of butler culture in Japan and enhancement of social recognition, and we will redouble our efforts toward establishing world-class butler service that fuses tradition and innovation.',
    related: [
      { to: '/en/news/raffles-butler-campaign', image: '/images/generated/jba-fs-115-news_raffles-london-campaign-v3.webp', aspect: 'aspect-[3/4]', alt: 'Related article: Raffles "The Butler Did It" Global Campaign', title: 'Raffles "The Butler Did It" Global Campaign', desc: 'Chapter 1 of the innovative brand campaign' },
      { to: '/en/news/apple-robot-butler', image: '/images/generated/jba-fs-116-news_raffles-london-campaign-v4.webp', aspect: 'aspect-video', alt: 'Related article: Apple Robot Butler Development Project', title: 'Apple Robot Butler Development Project', desc: 'The future of butler service opened by AI technology' }
    ]
  };

  const sources = [
    { label: 'Source', value: 'Raffles Hotels & Resorts (Accor Pressroom official announcement)', link: false },
    { label: 'Campaign Coverage', value: 'https://globetrender.com/2025/09/17/raffles-unveils-second-fashion-forward-butler-did-it-campaign/', link: true },
    { label: 'Accor Pressroom', value: 'https://press.accor.com/?p=53198', link: true },
    { label: 'Official Campaign Site', value: 'https://www.raffles.com/the-butler-did-it/', link: true },
    { label: 'Access Date', value: 'July 2026', link: false }
  ];

  const toc = [
    { id: 's-features', label: 'Campaign Chapter 2 Features' },
    { id: 's-cast', label: 'Glamorous New Cast' },
    { id: 's-hyper', label: 'Realization of Hyper-Personalization' },
    { id: 's-owo', label: 'The OWO Backdrop' },
    { id: 's-impact', label: 'Cultural and Social Impact' }
  ];

  return (
    <Layout>
      <title>Raffles London "The Butler Did It" Campaign Chapter 2 | Japan Butler Association</title>
      <meta name="description" content="Raffles Hotels & Resorts announces Chapter 2 of The Butler Did It campaign, set in London's historic The OWO building, showcasing hyper-personalized butler service through cinematic approach." />
      <link rel="canonical" href="https://butler-association.jp/en/news/raffles-london-campaign" />
      <link rel="alternate" hrefLang="ja" href="https://butler-association.jp/news/raffles-london-campaign" />
      <link rel="alternate" hrefLang="en" href="https://butler-association.jp/en/news/raffles-london-campaign" />

      <ArticleHero eyebrow="News / Industry" category={article.category} title={article.title} subtitle={article.subtitle} date={article.date} image={article.heroImage} />
      <div className="bg-white [&>div]:!pt-6">
        <Breadcrumb items={breadcrumbs} />
      </div>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-14">
          <div className="border-l-2 border-[#a38e65] pl-6 sm:pl-8">
            <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.24em] text-[11px] text-[#8f7a56] mb-3">Summary</span>
            <div className="space-y-4">
              {article.lead.split(/\n{2,}/).map((s) => s.trim()).filter(Boolean).map((s, i) => (
                <p key={i} className="jba-display text-[#1a2942] text-lg sm:text-xl leading-[1.95] tracking-[0.01em]">{s}</p>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="bg-white">
        <div className="max-w-6xl mx-auto px-6 pt-12 pb-4 lg:grid lg:grid-cols-[minmax(0,1fr)_17rem] lg:gap-14">
          <article className="w-full max-w-[760px] mx-auto lg:mx-0">
            <KeyPointsBox
              title="Article Highlights"
              points={[
                'Chapter Two of "The Butler Did It" launched on September 17, 2025, set at Raffles London at The OWO',
                'Tim Easton reprises the Butler role alongside a cast including Henry Golding, Oli Green, Jacquetta Wheeler, Stephanie Grainger and Amalie Gassman. Directed by Trey Laird, with photography by Dylan Don and styling by Robert Rabensteiner',
                "Campaign highlights include a private afternoon tea, a bespoke bedtime story and a royal wake-up call by the King's Piper — unexpected moments of service"
              ]}
            />

            {/* 01: Chapter 2 Features (existing body, unchanged) */}
            <SectionHeading id="s-features" en="Chapter Two">Campaign Chapter 2 Features</SectionHeading>
            <div className="space-y-6">
              <p className={P}>Chapter 2 of "The Butler Did It" campaign is set in The OWO (Old War Office), London's iconic historic building. This building, once used as the British government's War Office, is a prestigious structure now reborn as Raffles London.</p>
              <div className="space-y-5">
                <p className={P}>The campaign features British actor Henry Golding, known for "Crazy Rich Asians," expressing the essence of Raffles' butler service through cinematic direction.</p>
                <p className={P}>This visual work is highly regarded as a cultural piece pursuing the artistry of butler service, beyond mere advertising.</p>
              </div>
            </div>
            <SourceShot
              src="/images/news-sources/raffles-london-campaign-globetrender.jpg"
              alt="Screenshot of the Globetrender article covering Raffles' The Butler Did It campaign Chapter Two, showing the headline and campaign visual"
              media="Globetrender"
              url="https://globetrender.com/2025/09/17/raffles-unveils-second-fashion-forward-butler-did-it-campaign/"
            />

            <div className="my-10 -mx-6 sm:mx-0">
              <PullQuote quote="Set in the historic The OWO, the campaign renders the artistry of butler service through a cinematic lens." cite="Raffles London &quot;The Butler Did It&quot; Chapter Two" />
            </div>

            {/* Augmented: Star Cast */}
            <SectionHeading id="s-cast" en="Star Cast">Glamorous New Cast</SectionHeading>
            <div className="space-y-6">
              <div className="space-y-5">
                <p className={P}>Chapter Two sees Tim Easton reprise the Butler role alongside new guests including Henry Golding, Oli Green, Jacquetta Wheeler, Stephanie Grainger and Amalie Gassman.</p>
                <p className={P}>It was directed by Trey Laird, with photography by Dylan Don and styling by Robert Rabensteiner.</p>
              </div>
              <p className={P}>The Singapore storyline continues at The OWO, blending fashion-forward imagery with Raffles' signature personalised service.</p>
            </div>

            {/* 02: Hyper-Personalization (existing body, unchanged) */}
            <SectionHeading id="s-hyper" en="Hyper-Personalization">Realization of Hyper-Personalization</SectionHeading>
            <div className="space-y-6">
              <p className={P}>The core of the campaign is the concept of "hyper-personalization." This represents a service philosophy of deeply understanding each guest's personality, preferences, and lifestyle, providing completely customized experiences for each individual.</p>
              <div className="space-y-5">
                <p className={P}>Raffles butlers conduct detailed research before guest arrival, and throughout the stay, anticipate needs before guests verbalize them, creating experiences that exceed expectations.</p>
                <p className={P}>This level of service reaches an artistic realm that improves quality of life, beyond mere hospitality.</p>
              </div>
            </div>

            {/* Augmented: The OWO Backdrop */}
            <SectionHeading id="s-owo" en="The OWO">The OWO Backdrop</SectionHeading>
            <div className="space-y-6">
              <p className={P}>Set against the historic Old War Office walls, once trodden by spies, the campaign captures unexpected service moments such as a private afternoon tea, bedtime stories and a royal wake-up by the King's Piper.</p>
              <p className={P}>It showcases Raffles' butler tradition through a lens of British sophistication and intrigue at this landmark London property, rolled out worldwide.</p>
            </div>
            <SourceShot
              src="/images/news-sources/raffles-london-campaign-raffles.jpg"
              alt="Screenshot of the Raffles Hotels & Resorts official website page for the The Butler Did It campaign, presenting the Chapter Two cast and staging"
              media="Raffles Hotels & Resorts official site"
              url="https://www.raffles.com/the-butler-did-it/"
            />

            {/* 03: Cultural and Social Impact (existing body, unchanged) */}
            <SectionHeading id="s-impact" en="Cultural Impact">Cultural and Social Impact</SectionHeading>
            <div className="space-y-6">
              <div className="space-y-5">
                <p className={P}>This campaign plays an important role in reinterpreting the traditional profession of butler service in a modern context and enhancing its social value.</p>
                <p className={P}>It particularly functions as effective media conveying the appeal and possibilities of the butler profession to younger generations.</p>
              </div>
              <p className={P}>Additionally, by setting it in London, a historic city, it symbolically expresses the fusion of British butler culture tradition and modern innovation, showing new directions for international butler service development.</p>
            </div>
          </article>

          <aside className="hidden lg:block">
            <div className="sticky top-28 space-y-8">
              <nav aria-label="Contents" className="border border-[#e2ddd2] bg-[#f8f6f0] p-6">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Contents</span>
                <ul className="space-y-2.5">
                  {toc.map((t) => (
                    <li key={t.id}>
                      <a href={`#${t.id}`} className="text-[14px] text-[#3a4658] font-['Noto_Serif_JP',serif] leading-[1.6] hover:text-[#8f7a56] transition-colors border-l-2 border-transparent hover:border-[#a38e65] pl-3 -ml-3 block">{t.label}</a>
                    </li>
                  ))}
                </ul>
              </nav>

              <div className="border border-[#e2ddd2] p-6">
                <span className="block font-['Playfair_Display',serif] uppercase tracking-[0.22em] text-[11px] text-[#8f7a56] mb-4">Article Info</span>
                <dl className="space-y-3 text-[13.5px] font-['Noto_Serif_JP',serif]">
                  <div><dt className="text-[#8f7a56]">Announced</dt><dd className="text-[#333] mt-0.5">September 17, 2025</dd></div>
                  <div><dt className="text-[#8f7a56]">Setting</dt><dd className="text-[#333] mt-0.5">Raffles London at The OWO</dd></div>
                  <div><dt className="text-[#8f7a56]">Butler</dt><dd className="text-[#333] mt-0.5">Tim Easton</dd></div>
                  <div><dt className="text-[#8f7a56]">Category</dt><dd className="text-[#333] mt-0.5">{article.category}</dd></div>
                </dl>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pt-8 pb-14">
          <CommentBox eyebrow="From the Association" heading="Comment from Japan Butler Association" body={article.comment} />
        </div>
      </section>

      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 pb-14">
          <SourceCard heading="Source" rows={sources} />
        </div>
      </section>

      <RelatedCards heading="Related Articles" items={article.related} readLabel="Read" backHref="/en/news" backLabel="Back to News List" />
    </Layout>
  );
}
